import { z } from 'zod';
import { TaskState } from '@/types/uploads';

export const exportStatusSchema = z.enum(['pending', 'processing', 'completed', 'failed', 'nothing_to_export']);

export const exportSchema = z.object({
  id: z.string(),
  status: exportStatusSchema,
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  assetsCount: z.number(),
  estimatedSizeBytes: z.number(),
  progressPercent: z.number().default(0),
  options: z.object({
    includeChildren: z.boolean(),
    variantIds: z.array(z.string()),
    includeOriginalFile: z.boolean(),
    includeMetadata: z.boolean(),
  }),
  name: z.string().optional(),
  links: z.object({
    self: z.string(),
    download: z.string(),
  }),
});

export type Export = z.infer<typeof exportSchema>;
export type ExportStatus = z.infer<typeof exportStatusSchema>;

// Share-scoped exports reuse the export shape, but the backend ShareExportResource
// omits the `options` block (guests have no caller-controlled export options).
export const shareExportSchema = exportSchema.omit({ options: true });

export type ShareExport = z.infer<typeof shareExportSchema>;

export type ShareExportRequest = {
  nodeIds: Array<string>;
  variantIds: Array<string>;
  includeOriginalFile?: boolean;
  dryRun: boolean;
};

export const TERMINAL_STATUSES: Array<ExportStatus> = ['completed', 'failed', 'nothing_to_export'];

export type ExportRequest = {
  nodeIds: Array<string>;
  dryRun: boolean;
  includeChildren?: boolean;
  variantIds?: Array<string>;
  includeOriginalFile?: boolean;
  includeMetadata?: boolean;
};

export const exportStatusToTaskState = (status: ExportStatus): TaskState => {
  // nothing_to_export maps to Finished only for union exhaustiveness — it never
  // produces a bar (the trigger toasts it instead of calling addExport).
  // No default case: TypeScript enforces the switch is exhaustive over ExportStatus.
  // eslint-disable-next-line default-case
  switch (status) {
    case 'completed':
    case 'nothing_to_export':
      return TaskState.Finished;
    case 'failed':
      return TaskState.Errored;
    case 'pending':
    case 'processing':
      return TaskState.InProgress;
  }
};

export interface ExportErrorInterface {
  code: string;
  message: string;
  assetsCount: number;
  estimatedSizeBytes: number;
  status: number;
}

export type NothingToExportError = ExportErrorInterface & {
  code: 'nothing_to_export';
};

export type ExportTooLargeError = ExportErrorInterface & {
  code: 'export_too_large';
  maximumSizeBytes: number;
};

export type ExportExpiredError = ExportErrorInterface & {
  code: 'export_expired';
};

export type UnknownExportError = ExportErrorInterface & {
  code: 'unknown';
};

export type KnownExportError = NothingToExportError | ExportTooLargeError | ExportExpiredError;

export type ExportError = KnownExportError | UnknownExportError;

export function isKnownExportError(error: unknown): error is KnownExportError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error.code === 'nothing_to_export' || error.code === 'export_too_large' || error.code === 'export_expired')
  );
}

export function isExportExpiredError(error: unknown): error is ExportExpiredError {
  return typeof error === 'object' && error !== null && (error as Record<string, unknown>).code === 'export_expired';
}
